import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { trackEvent } from '../../../../helpers/analytics';
import { getColumns } from '../../../../pages/contactsExtended/hooks/useContactsTableRows';
import { getContactsColumnsKey } from '../../../../helpers/storeKeys';
import { getItem } from '../../../../helpers/store';
import { jobApplicantsJobTnSelector } from '../../../../redux/selectors';
import { useGetCustomFieldsQuery } from '../../../../services/customFieldsV2';

const serializeFields = (fields) => fields.map((field) => ({ fieldId: field.accessor, visible: field.show }));

const deserializeFields = (apiFields, availableFields) => {
  if (!apiFields || apiFields.length === 0) {
    return availableFields.map((field) => ({ accessor: field.accessor, show: true }));
  }

  const parsedFields = apiFields
    .map((field) => ({
      accessor: field.fieldId,
      show: field.visible,
    }))
    .filter((field) => availableFields.some((col) => col.accessor === field.accessor));

  const savedAccessors = new Set(parsedFields.map((f) => f.accessor));
  availableFields.forEach((field) => {
    if (!savedAccessors.has(field.accessor)) {
      parsedFields.push({ accessor: field.accessor, show: true });
    }
  });

  return parsedFields;
};

export const useFieldVisibility = ({ list, activeNetwork, updateView, viewId }) => {
  const joinTnFromJB = useSelector(jobApplicantsJobTnSelector);

  const listIdString = list?.id ? String(list.id) : null;
  const { data: customFields = [] } = useGetCustomFieldsQuery(
    {
      includeLists: !!listIdString,
      ...(listIdString ? { sharedListId: listIdString } : undefined),
    },
    { skip: !listIdString },
  );

  const availableFields = useMemo(() => {
    const columns = getColumns({
      type: 'public',
      listId: list?.id,
      contactCount: 0,
      tnHidden: true, // Always hide TN fields in public view
      customFields,
    });

    return columns.filter((col) => col.accessor !== 'name' && !col.hideOnTableColumn);
  }, [list?.id, customFields]);

  const fieldsState = useMemo(() => {
    if (!list?.views) {
      return availableFields.map((field) => ({ accessor: field.accessor, show: true }));
    }

    const sharedView = list.views.find((view) => view.shareable === true);
    if (!sharedView?.fieldSettings) {
      return availableFields.map((field) => ({ accessor: field.accessor, show: true }));
    }

    return deserializeFields(sharedView.fieldSettings, availableFields);
  }, [list?.views, availableFields]);

  const onFieldsChange = useCallback(
    async (newFields) => {
      if (!updateView || !viewId) return;

      const serialized = serializeFields(newFields);

      try {
        await updateView({
          sharedListId: list.id,
          id: viewId,
          data: {
            fieldSettings: serialized,
          },
        });

        const visibleCount = newFields.filter((f) => f.show).length;
        trackEvent('contacts:list:change_field_visibility', {
          collection_id: activeNetwork.id,
          list_id: list.id,
          visible_fields_count: visibleCount,
        });
      } catch (error) {
        // Error already handled by updateViewWithToast
      }
    },
    [activeNetwork.id, list.id, updateView, viewId],
  );

  const getInitialFields = useCallback(() => {
    // Get the local storage key for the list detail view (matches useExtendContacts key)
    const detailViewColumnsKey = getContactsColumnsKey({
      activeNetwork,
      listId: list?.id,
      joinTnFromJB,
    });

    const detailViewColumnsRaw = getItem(detailViewColumnsKey);
    let detailViewColumns = null;

    if (detailViewColumnsRaw) {
      try {
        detailViewColumns = JSON.parse(detailViewColumnsRaw);
      } catch (e) {
        detailViewColumns = null;
      }
    }

    if (detailViewColumns && Array.isArray(detailViewColumns)) {
      const orderedFields = detailViewColumns
        .filter((col) => availableFields.some((f) => f.accessor === col.accessor))
        .map((col) => ({
          accessor: col.accessor,
          show: col.show !== false, // Default to true if not explicitly false
        }));

      const orderedAccessors = new Set(orderedFields.map((f) => f.accessor));
      availableFields.forEach((field) => {
        if (!orderedAccessors.has(field.accessor)) {
          orderedFields.push({
            accessor: field.accessor,
            show: false, // New fields not in detail view are hidden by default
          });
        }
      });

      return orderedFields;
    }

    // Fallback: all available fields visible
    return availableFields.map((field) => ({ accessor: field.accessor, show: true }));
  }, [activeNetwork, list?.id, availableFields, joinTnFromJB]);

  const getInitialFieldsSerialized = useCallback(() => serializeFields(getInitialFields()), [getInitialFields]);

  return {
    availableFields,
    fieldsState,
    onFieldsChange,
    getInitialFields,
    getInitialFieldsSerialized,
  };
};
